import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

function collectScrollContainers(fromEl) {
  const out = [];
  if (!fromEl) return out;
  let node = fromEl.parentElement;
  while (node && node !== document.documentElement) {
    const cs = window.getComputedStyle(node);
    const ox = cs.overflowX;
    const oy = cs.overflowY;
    if (/(auto|scroll|overlay)/.test(ox) || /(auto|scroll|overlay)/.test(oy)) {
      out.push(node);
    }
    node = node.parentElement;
  }
  return out;
}

/**
 * Text field with dropdown of saved suggestions; typing filters the list and free text is always allowed.
 *
 * @param {object} props
 * @param {string} props.value
 * @param {(next: string) => void} props.onChange
 * @param {string[]} props.suggestions
 */
export default function SavedLocationCombobox({
  id,
  value,
  onChange,
  suggestions = [],
  onPick,
  onBlurPersist,
  disabled = false,
  placeholder = "Address or conference link",
  maxVisibleItems = 6,
  style,
  className,
  title,
  autoFocus = false,
}) {
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const menuPortalRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuFixed, setMenuFixed] = useState(null);
  const listboxId = useId();

  const list = useMemo(() => {
    const raw = Array.isArray(suggestions) ? suggestions : [];
    const seen = new Set();
    const out = [];
    for (const s of raw) {
      const t = s != null ? String(s).trim() : "";
      if (!t) continue;
      const key = t.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(t);
    }
    return out;
  }, [suggestions]);

  const closeMenu = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    const closeOnOutside = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      if (menuPortalRef.current?.contains(e.target)) return;
      closeMenu();
    };
    const closeOnEsc = (e) => {
      if (e.key === "Escape") closeMenu();
    };
    document.addEventListener("mousedown", closeOnOutside);
    window.addEventListener("keydown", closeOnEsc);
    return () => {
      document.removeEventListener("mousedown", closeOnOutside);
      window.removeEventListener("keydown", closeOnEsc);
    };
  }, [open, closeMenu]);

  useLayoutEffect(() => {
    if (!open || !rootRef.current) {
      if (!open) setMenuFixed(null);
      return;
    }
    const itemH = 34;
    const maxItems = Math.max(1, Number(maxVisibleItems) || 6);
    const desiredHeight = maxItems * itemH + 10;

    const update = () => {
      const anchor = inputRef.current || rootRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const preferredTop = rect.bottom + 4;
      const clampedTop = Math.min(preferredTop, window.innerHeight - desiredHeight - 4);
      const top = Math.max(4, clampedTop);
      setMenuFixed({
        left: rect.left,
        width: rect.width,
        top,
        maxHeight: `${maxItems * itemH}px`,
      });
    };

    update();
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      update();
      raf2 = requestAnimationFrame(update);
    });

    const scrollParents = collectScrollContainers(rootRef.current);
    scrollParents.forEach((n) => n.addEventListener("scroll", update, { passive: true }));
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
      scrollParents.forEach((n) => n.removeEventListener("scroll", update));
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, maxVisibleItems]);

  const wrapperStyle = {
    position: "relative",
    width: "100%",
    minWidth: 0,
  };

  const baseFieldStyle = {
    width: "100%",
    minWidth: 0,
    padding: "6px 34px 6px 10px",
    fontSize: "14px",
    borderRadius: "4px",
    border: "1px solid #dee2e6",
    boxSizing: "border-box",
    outline: "none",
    background: disabled ? "#f1f3f5" : "#fff",
    cursor: disabled ? "not-allowed" : "text",
  };

  const mergedInputStyle = {
    ...baseFieldStyle,
    ...(style || {}),
    paddingRight: "34px",
  };

  const menuBoxStyle = {
    border: "1px solid #ced4da",
    borderRadius: "6px",
    background: "#fff",
    boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
    overflowY: "auto",
    overflowX: "hidden",
    zIndex: 11000,
  };

  const renderMenu = () => (
    <div
      id={listboxId}
      role="listbox"
      aria-label="Saved locations"
      style={{ ...menuBoxStyle, maxHeight: menuFixed?.maxHeight || `${Math.max(1, Number(maxVisibleItems) || 6) * 34}px` }}
    >
      {list.map((opt) => (
        <div
          key={opt}
          role="option"
          aria-selected={opt === value}
          tabIndex={0}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            if (onChange) onChange(opt);
            if (typeof onPick === "function") onPick(opt);
            closeMenu();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              if (onChange) onChange(opt);
              if (typeof onPick === "function") onPick(opt);
              closeMenu();
            }
          }}
          style={{
            width: "100%",
            minHeight: 32,
            background: opt === value ? "#e7f1ff" : "#fff",
            display: "flex",
            alignItems: "center",
            textAlign: "left",
            padding: "6px 10px",
            fontSize: "14px",
            lineHeight: 1.2,
            color: "#212529",
            cursor: "pointer",
            boxSizing: "border-box",
            userSelect: "none",
          }}
        >
          {opt}
        </div>
      ))}
      {list.length === 0 && (
        <div style={{ padding: "8px 10px", fontSize: 13, color: "#6c757d" }}>
          No saved locations yet. Type an address or link.
        </div>
      )}
    </div>
  );

  return (
    <div ref={rootRef} style={wrapperStyle} className={className}>
      <input
        id={id}
        ref={inputRef}
        type="text"
        value={value}
        disabled={disabled}
        title={title}
        autoFocus={autoFocus}
        placeholder={placeholder}
        onChange={(e) => {
          if (disabled) return;
          if (onChange) onChange(e.target.value);
        }}
        onFocus={() => {
          if (disabled) return;
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (disabled) return;
          if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
            e.preventDefault();
            setOpen(true);
            return;
          }
          if (open && e.key === "Enter") {
            // User finished typing a new value; close dropdown immediately.
            // Prevent form submit (EventForm) when pressing Enter in this field.
            e.preventDefault();
            e.stopPropagation();
            closeMenu();
            if (typeof onBlurPersist === "function") onBlurPersist(value);
          }
        }}
        onBlur={() => {
          if (typeof onBlurPersist === "function") onBlurPersist(value);
        }}
        style={mergedInputStyle}
        role="combobox"
        aria-controls={listboxId}
        aria-expanded={open}
        aria-autocomplete="list"
        autoComplete="off"
        spellCheck={false}
      />
      <button
        type="button"
        aria-label="Show saved locations"
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
          setTimeout(() => inputRef.current?.focus?.(), 0);
        }}
        disabled={disabled}
        style={{
          position: "absolute",
          right: 4,
          top: 0,
          bottom: 0,
          width: 28,
          border: "none",
          background: "transparent",
          cursor: disabled ? "not-allowed" : "pointer",
          color: "#6c757d",
          fontSize: 12,
        }}
      >
        ▾
      </button>

      {open &&
        menuFixed != null &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={menuPortalRef}
            style={{
              position: "fixed",
              left: menuFixed.left,
              top: menuFixed.top,
              width: menuFixed.width,
              zIndex: 11000,
            }}
          >
            {renderMenu()}
          </div>,
          document.body
        )}
    </div>
  );
}
